import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import {
  useMarkNotificationsRead,
  useNotifications,
  useUnreadNotificationsCount,
} from '@/lib/api/notifications';

import { NotificationFeed } from './notification-feed';
import { selectIdsToMark } from './notification-read-queue';

const UNREAD_DISPLAY_CAP = 9;

export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const { orgSlug } = useOrgNavigate();
  const [open, setOpen] = useState(false);
  const markedIdsRef = useRef<Set<string>>(new Set());

  const { data: notifications, isLoading, isError } = useNotifications();
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const markNotificationsRead = useMarkNotificationsRead();

  const unreadIds = (notifications ?? [])
    .filter((notification) => notification.readAt === null)
    .map((notification) => notification.id);
  const unreadIdsKey = unreadIds.join(',');

  useEffect(() => {
    if (!open) {
      markedIdsRef.current = new Set();
      return;
    }

    const idsToMark = selectIdsToMark(unreadIds, markedIdsRef.current);
    if (idsToMark.length === 0) return;

    idsToMark.forEach((id) => markedIdsRef.current.add(id));
    markNotificationsRead.mutate({ notificationIds: idsToMark });
  }, [open, unreadIdsKey]);

  const displayCount = unreadCount > UNREAD_DISPLAY_CAP ? `${UNREAD_DISPLAY_CAP}+` : unreadCount;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8 rounded-md"
          aria-label={t('bell.ariaLabel', { count: unreadCount })}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-4 min-w-4 justify-center rounded-full px-1 text-[10px] leading-none"
            >
              {displayCount}
            </Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <NotificationFeed
          notifications={notifications}
          isLoading={isLoading}
          isError={isError}
          orgSlug={orgSlug}
          onNavigate={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
